import * as esbuild from "esbuild-wasm";
import wasmUrl from "esbuild-wasm/esbuild.wasm?url";

/**
 * Turns a Claude artifact (a raw .jsx/.tsx file) into a single self-contained
 * HTML page, entirely in the consultant's browser.
 *
 * Why this exists: what you download from Claude is a React component, and a
 * browser cannot run one. Before this, sharing an artifact meant installing
 * Node, wiring up Vite and running a build — which is not a thing to ask of a
 * consultant who wants to send a colleague a link.
 *
 * Why in the browser rather than a Lambda: the output has to be one file with
 * everything inlined (tfg-pitch serves exactly one object per report), and
 * doing it here means no new infrastructure, no change to the upload API, and
 * no server that executes or bundles untrusted code.
 *
 * Two constraints from how reports are served, both of which produce a blank
 * white page rather than an error if you get them wrong:
 *
 *   1. NO ES MODULES. /reports/* carries `Content-Security-Policy: sandbox
 *      allow-scripts`, which gives the page an opaque origin. Module scripts
 *      do not execute in an opaque origin — they are silently skipped. So we
 *      bundle to IIFE and emit a classic <script>.
 *
 *   2. NOTHING EXTERNAL. Any src= or href= pointing at a sibling file 404s,
 *      because only index.html is uploaded. Everything is inlined.
 */

/** Pinned so a package that pulls its own React doesn't end up with a second copy. */
const REACT_VERSION = "18.3.1";
const ESM_HOST = "https://esm.sh";

/**
 * Tailwind's browser build. Artifacts are written in Tailwind classes and look
 * broken without it.
 *
 * This specific build is `index.global.js` — an IIFE with no top-level imports
 * and no fetch() calls, verified before choosing it. That matters twice over:
 * it runs in the sandbox (a module build would not), and once inlined the page
 * has no runtime dependency on a CDN being reachable from the client's network.
 *
 * v4 rather than a prebuilt v3 stylesheet because v3 is JIT-only and ships no
 * dist CSS; the v2 full build exists but is 2.9 MB and misses v3/v4 utilities.
 */
const TAILWIND_URL = "https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4";

const ENTRY = "__tfg_entry__";
const ARTIFACT = "__tfg_artifact__";

let initPromise: Promise<void> | null = null;

async function ensureEsbuild(): Promise<void> {
  if (!initPromise) {
    initPromise = esbuild.initialize({ wasmURL: wasmUrl });
  }
  await initPromise;
}

export interface BuildProgress {
  (stage: string): void;
}

export class ArtifactBuildError extends Error {
  constructor(
    message: string,
    /** Import specifiers we could not resolve, when that's the cause. */
    public readonly unresolved: string[] = [],
  ) {
    super(message);
    this.name = "ArtifactBuildError";
  }
}

/** True for the file types Claude hands you when you download an artifact. */
export function isArtifactSource(file: File): boolean {
  return /\.(jsx|tsx)$/i.test(file.name);
}

/**
 * Resolves bare imports through esm.sh and fetches everything over the network.
 *
 * esbuild in the browser has no filesystem, so every module — the artifact
 * itself and each dependency — is served by this plugin.
 */
export function esmUrlFor(spec: string): string {
  if (spec === "react" || spec.startsWith("react/")) {
    return `${ESM_HOST}/react@${REACT_VERSION}${spec.slice("react".length)}`;
  }
  if (spec === "react-dom" || spec.startsWith("react-dom/")) {
    return `${ESM_HOST}/react-dom@${REACT_VERSION}${spec.slice("react-dom".length)}?external=react`;
  }
  // Everything else — recharts, lucide-react, xlsx — must not bring its own
  // React or react-dom either.
  return `${ESM_HOST}/${spec}?external=react,react-dom`;
}

function networkPlugin(source: string, isTsx: boolean, unresolved: string[]): esbuild.Plugin {
  /**
   * Maps a bare specifier to an esm.sh URL, keeping React to ONE copy.
   *
   * Getting this wrong produces the least obvious failure in the whole flow:
   * the page renders blank and the console says
   *
   *     Cannot read properties of null (reading 'useState')
   *
   * That is React's hook dispatcher being null because the component and the
   * renderer are holding *different* React instances. The first version of this
   * resolver caused exactly that — `react` and `react-dom/client` went to
   * different esm.sh URLs, and react-dom quietly pulled in its own React, so
   * two copies ended up in one bundle.
   *
   * The fix is `?external=react`: esm.sh then emits a bare `import … from
   * "react"`, which comes back through this same resolver and lands on the one
   * pinned URL. React subpaths (jsx-runtime, used by the automatic JSX
   * transform) have to resolve to that same copy too.
   */
  const esmUrl = esmUrlFor;

  return {
    name: "tfg-network",
    setup(build) {
      build.onResolve({ filter: new RegExp(`^${ENTRY}$`) }, () => ({
        path: ENTRY,
        namespace: "tfg",
      }));
      build.onResolve({ filter: new RegExp(`^${ARTIFACT}$`) }, () => ({
        path: ARTIFACT,
        namespace: "tfg",
      }));

      build.onLoad({ filter: /.*/, namespace: "tfg" }, (args) => {
        if (args.path === ARTIFACT) {
          return { contents: source, loader: isTsx ? "tsx" : "jsx", resolveDir: "/" };
        }
        // Mounts whatever the artifact default-exports. Claude artifacts are
        // written as a default-exported component, which is the one shape we
        // can rely on.
        return {
          contents: `
            import React from "react";
            import { createRoot } from "react-dom/client";
            import App from "${ARTIFACT}";
            const el = document.getElementById("root");
            createRoot(el).render(React.createElement(React.StrictMode, null, React.createElement(App)));
          `,
          loader: "jsx",
          resolveDir: "/",
        };
      });

      // Bare specifier — react, recharts, lucide-react, xlsx, …
      build.onResolve({ filter: /^[^./]/ }, (args) => ({
        path: esmUrl(args.path),
        namespace: "http",
      }));

      // Relative import from inside something we already fetched.
      build.onResolve({ filter: /.*/, namespace: "http" }, (args) => ({
        path: new URL(args.path, args.importer).toString(),
        namespace: "http",
      }));

      build.onLoad({ filter: /.*/, namespace: "http" }, async (args) => {
        const res = await fetch(args.path);
        if (!res.ok) {
          unresolved.push(args.path.replace(`${ESM_HOST}/`, "").split("?")[0]!);
          throw new Error(`could not fetch ${args.path} (${res.status})`);
        }
        const contents = await res.text();
        const loader: esbuild.Loader = args.path.endsWith(".css") ? "css" : "js";
        return { contents, loader };
      });
    },
  };
}

/**
 * Builds the artifact and returns a complete HTML document.
 *
 * Throws ArtifactBuildError with a message the consultant can act on — the
 * common failure is an import that has no public package behind it, which
 * needs a person to decide what to do, not a retry.
 */
export async function buildArtifact(
  file: File,
  onProgress: BuildProgress = () => {},
): Promise<string> {
  const source = await file.text();
  const isTsx = /\.tsx$/i.test(file.name);
  const unresolved: string[] = [];

  onProgress("Starting the bundler…");
  await ensureEsbuild();

  onProgress("Fetching dependencies…");
  let js: string;
  try {
    const result = await esbuild.build({
      entryPoints: [ENTRY],
      bundle: true,
      write: false,
      // IIFE, not ESM: module scripts do not execute in the sandbox's opaque
      // origin. This is the difference between a working page and a blank one.
      format: "iife",
      target: "es2019",
      minify: true,
      jsx: "automatic",
      define: { "process.env.NODE_ENV": '"production"' },
      plugins: [networkPlugin(source, isTsx, unresolved)],
      logLevel: "silent",
    });
    js = result.outputFiles?.[0]?.text ?? "";
  } catch (err) {
    if (unresolved.length > 0) {
      const list = [...new Set(unresolved)].join(", ");
      throw new ArtifactBuildError(
        `Could not resolve: ${list}. Artifacts that import project-local files ` +
          `(for example "@/components/ui/button") can't be built here — those ` +
          `aren't published packages. Paste those components into the artifact ` +
          `file, or ask whoever wrote it for a self-contained version.`,
        [...new Set(unresolved)],
      );
    }
    throw new ArtifactBuildError(
      err instanceof Error ? err.message : "The artifact could not be compiled.",
    );
  }

  if (!js.trim()) {
    throw new ArtifactBuildError("The bundler produced no output.");
  }

  onProgress("Inlining styles…");
  const tailwind = await fetchTailwind();

  onProgress("Assembling the page…");
  return renderDocument({ title: file.name.replace(/\.(jsx|tsx)$/i, ""), tailwind, js });
}

async function fetchTailwind(): Promise<string> {
  const res = await fetch(TAILWIND_URL);
  if (!res.ok) {
    throw new ArtifactBuildError(
      `Could not fetch Tailwind (${res.status}). Check your network and try again — ` +
        `it's only needed while building; the finished page doesn't depend on it.`,
    );
  }
  return res.text();
}

function renderDocument({
  title,
  tailwind,
  js,
}: {
  title: string;
  tailwind: string;
  js: string;
}): string {
  // Both scripts are classic <script> tags on purpose. See the note at the top
  // of this file: type="module" silently does not run under the report CSP.
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="robots" content="noindex,nofollow">
<title>${escapeHtml(title)}</title>
<style>html,body,#root{min-height:100%}body{margin:0}</style>
<script>${tailwind}</script>
</head>
<body>
<div id="root"></div>
<script>${js}</script>
</body>
</html>`;
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      (({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }) as const)[
        c as "&"
      ]!,
  );
}

/** File types the upload and update pickers accept. */
export const UPLOAD_ACCEPT = ".html,.jsx,.tsx,text/html";

/** Server-side cap, mirrored here so we fail before a pointless upload. */
const MAX_BYTES = 25 * 1024 * 1024;

/**
 * Returns the file to actually upload: a Claude artifact compiled to a
 * self-contained page, or the original if it is already HTML.
 *
 * Shared by the upload page and the dashboard's Update button. They used to
 * differ — Update took the raw file and PUT it, which for a .jsx would have
 * published React source that no browser can run, and its picker would not
 * offer one anyway.
 */
export async function prepareUpload(
  file: File,
  onProgress: BuildProgress = () => {},
): Promise<File> {
  const out = isArtifactSource(file)
    ? new File(
        [await buildArtifact(file, onProgress)],
        file.name.replace(/\.(jsx|tsx)$/i, ".html"),
        {
          type: "text/html",
        },
      )
    : file;

  if (out.size > MAX_BYTES) {
    throw new ArtifactBuildError(
      `The page is ${(out.size / 1024 / 1024).toFixed(1)} MB, over the 25 MB limit.`,
    );
  }
  return out;
}
